import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  forwardRef
} from 'react';
import DataGridView from '../view/DataGridView';
// import cloneMachine from '../../utils/cloneMachine';
import { cloneDeep } from 'lodash';
const cloneMachine = arr => {
  return cloneDeep(arr);
};

/**
 *
 * @param {Array} columns - Array of objects, each object should have "id" - unique id, "display"- auto translated string or react node, "customCSS" - optional, custom CSS for the column cell
 * @param {Array} rows - Array of objects, each object should have keys corespponding to  the coulmns ids with auto translated string or react node. Example: columns: [{id: "A", ...}, {id: "B", ...}, {id: "C", ...}]; rows: [{A: "row 0 column A", B: "row 0 column B", C: "row 0 column C"}]
 * @param {Object} sort - Object that holds id and value of the column hat is being sorted. value can either be asc or desc
 * @param {Function} onSortChange - Function to be called when sort event was triggered. Signature: (colId, newSortValue) => {}, (newSortValue is either "asc" || "desc")
 * @param {Boolean} loading - If true, the grid loader will show instead of the data
 * @param {React Component} noRecordsCmp - Pass here a react component that will be displayed instead of the default "no records" text when there are no records to display
 * @param {String} customCSS - CustomCSS for the grid
 * @param {String} customCellCSS - CustomCSS for the grid cells
 * @param {Boolean} internalScrolling - If true - scrolling will be inside the grid with sticky headers
 * @param {Integer} bottomPadding - If internalScrolling is true, add a constant number representing the space from the bottom of the page (useally footer height). Use this to discard page scroll bar
 * @returns
 */
const DataGridEngine = forwardRef(
  (
    {
      columns: givenColumns = [],
      rows: givenRows = [],
      sort: givenSort,
      onSortChange: givenOnSortChange,
      loading,
      noRecordsText,
      customCSS,
      customCellCSS,
      customDividerCSS,
      internalScrolling,
      bottomPadding = 0,
      ...otherProps
    },
    ref
  ) => {
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [sort, setSort] = useState({});
    const [_colsSettings, setColsSettings] = useState([]);
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);
    const [listMaxHeight, setListMaxHeight] = useState('auto');

    const tableRef = useRef(null);

    const handleResize = useCallback(() => {
      setScreenHeight(window.innerHeight);
    }, []);

    useEffect(() => {
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    useEffect(() => {
      // init

      const mappedColsSettings = {};
      _colsSettings.forEach(cs => (mappedColsSettings[cs.id] = cs));

      const __colsSettings = givenColumns.map((c, i) => ({
        id: c.id,
        width: c.width
          ? c.width
          : mappedColsSettings[c.id]?.width
          ? mappedColsSettings[c.id]?.width
          : '1fr',
        order: i
      }));

      setColumns(givenColumns);
      setRows(givenRows);
      setColsSettings(__colsSettings);
    }, [givenColumns, givenRows]);

    useEffect(() => {
      if (!tableRef.current) {
        return;
      }

      if (!internalScrolling) {
        setListMaxHeight('auto');
        return;
      }

      // Gotta have this set max-heigh "hack" to calculate true size of the grid
      tableRef.current.style['max-height'] = '1px';
      setTimeout(() => {
        tableRef.current.style['max-height'] = 'inherit';
      }, 5);

      /**
       * We can't use the overflow/flex method to create scroll
       * because we need to display drop shadow for the container.
       * Due to that limitation we need to use the old method of max-height.
       * This method is not responsive, therfore we need to make it responsive with js.
       *
       */
      const listRect = tableRef.current.getBoundingClientRect();

      const maxHeight = screenHeight - bottomPadding - listRect.y;

      setListMaxHeight(maxHeight);
    }, [screenHeight, bottomPadding, internalScrolling]);

    const onSortChange = useCallback(
      colId => {
        const newSortValue = sort.value === 'desc' ? 'asc' : 'desc';
        setSort({ id: colId, value: newSortValue });

        if (givenOnSortChange) {
          givenOnSortChange(colId, newSortValue);
          return;
        }
      },
      [sort, givenOnSortChange, rows]
    );

    // Need to sort them for grid layout
    const colsSettings = useMemo(() => {
      return [..._colsSettings.sort((a, b) => a.order - b.order)];
    }, [_colsSettings]);

    const orderedColumns = useMemo(() => {
      const mappedColSettings = {};
      colsSettings.forEach(cs => (mappedColSettings[cs.id] = cs));

      return cloneMachine(columns).sort(
        (a, b) =>
          mappedColSettings[a.id]?.order - mappedColSettings[b.id]?.order
      );
    }, [colsSettings, columns]);

    const viewProps = {
      columns: orderedColumns,
      rows,
      tableRef,
      sort: givenSort || sort,
      onSortChange,
      sortable: !!givenOnSortChange,
      loading,
      colsSettings,
      customCSS,
      customCellCSS,
      customDividerCSS,
      noRecordsText,
      otherProps,
      listMaxHeight
    };

    return <DataGridView {...viewProps} ref={ref} />;
  }
);

export default DataGridEngine;
